import { useState, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { calendarEventsApi, storesApi } from '../services/api';
import type { CalendarEvent, CalendarEventCreateRequest, CalendarEventUpdateRequest, PlaceSearchResult } from 'shared';

interface Props {
  event?: CalendarEvent | null;
  defaultDate?: string;
  onClose: () => void;
}

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'call', label: 'Call' },
  { value: 'other', label: 'Other' },
] as const;

export function CalendarEventModal({ event, defaultDate, onClose }: Props) {
  const queryClient = useQueryClient();
  const isEditing = !!event;

  const defaultDateStr = defaultDate ?? new Date().toISOString().split('T')[0];

  const [title, setTitle] = useState(event?.title ?? '');
  const [eventType, setEventType] = useState<'meeting' | 'lunch' | 'call' | 'other'>(event?.event_type ?? 'meeting');
  const [relatedStoreId, setRelatedStoreId] = useState(event?.related_store_id ?? '');
  const [contactName, setContactName] = useState(event?.contact_name ?? '');
  const [locationName, setLocationName] = useState(event?.location_name ?? '');
  const [locationAddress, setLocationAddress] = useState(event?.location_address ?? '');
  const [locationLat, setLocationLat] = useState<number | null>(event?.location_lat ?? null);
  const [locationLng, setLocationLng] = useState<number | null>(event?.location_lng ?? null);
  const [date, setDate] = useState(event ? event.start_datetime.split('T')[0] : defaultDateStr);
  const [time, setTime] = useState(() => {
    if (event) return event.start_datetime.split('T')[1]?.slice(0, 5) ?? '09:00';
    return '09:00';
  });
  const [duration, setDuration] = useState(event?.duration_minutes ?? 60);
  const [notes, setNotes] = useState(event?.notes ?? '');

  // Location search state
  const [locationQuery, setLocationQuery] = useState(event?.location_name ?? '');
  const [locationResults, setLocationResults] = useState<PlaceSearchResult[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const locationDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  // Selected store lat/lng for location search center
  const selectedStore = stores.find((s) => s.id === relatedStoreId);

  const searchLocation = (q: string) => {
    if (locationDebounce.current) clearTimeout(locationDebounce.current);
    if (!q.trim() || q.length < 2) { setLocationResults([]); return; }

    locationDebounce.current = setTimeout(async () => {
      setLocationSearching(true);
      try {
        const location = selectedStore ? { lat: selectedStore.lat, lng: selectedStore.lng } : undefined;
        const results = await storesApi.search(q, location);
        setLocationResults(results);
      } catch {
        setLocationResults([]);
      } finally {
        setLocationSearching(false);
      }
    }, 400);
  };

  const selectLocation = (place: PlaceSearchResult) => {
    setLocationName(place.name);
    setLocationAddress(place.address);
    setLocationLat(place.lat);
    setLocationLng(place.lng);
    setLocationQuery(place.name);
    setLocationResults([]);
  };

  const createMutation = useMutation({
    mutationFn: (data: CalendarEventCreateRequest) => calendarEventsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CalendarEventUpdateRequest) => calendarEventsApi.update(event!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => calendarEventsApi.delete(event!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const startDatetime = `${date}T${time}:00`;

    if (isEditing) {
      updateMutation.mutate({
        title, event_type: eventType,
        related_store_id: relatedStoreId || undefined,
        contact_name: contactName || undefined,
        location_name: locationName || undefined,
        location_address: locationAddress || undefined,
        location_lat: locationLat ?? undefined,
        location_lng: locationLng ?? undefined,
        start_datetime: startDatetime,
        duration_minutes: duration,
        notes: notes || undefined,
      });
    } else {
      createMutation.mutate({
        title, event_type: eventType,
        related_store_id: relatedStoreId || undefined,
        contact_name: contactName || undefined,
        location_name: locationName || undefined,
        location_address: locationAddress || undefined,
        location_lat: locationLat ?? undefined,
        location_lng: locationLng ?? undefined,
        start_datetime: startDatetime,
        duration_minutes: duration,
        notes: notes || undefined,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Event' : 'Add Event'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Lunch with Sleep Mart owner"
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Event type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="flex gap-2">
              {EVENT_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setEventType(t.value)}
                  className={`flex-1 py-1.5 text-sm rounded-md border ${eventType === t.value ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Related store */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Related Store (optional)</label>
            <select
              value={relatedStoreId}
              onChange={(e) => setRelatedStoreId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— None —</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Contact name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name (optional)</label>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="e.g. John Smith"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Location search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
            <input
              type="text"
              value={locationQuery}
              onChange={(e) => {
                setLocationQuery(e.target.value);
                setLocationName(e.target.value);
                setLocationLat(null);
                setLocationLng(null);
                searchLocation(e.target.value);
              }}
              placeholder="Search restaurant or venue..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {locationSearching && (
              <div className="absolute right-3 top-9 text-xs text-gray-400">Searching...</div>
            )}
            {locationResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {locationResults.map((place) => (
                  <button
                    key={place.place_id}
                    type="button"
                    onClick={() => selectLocation(place)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                  >
                    <div className="font-medium text-gray-900">{place.name}</div>
                    <div className="text-xs text-gray-500">{place.address}</div>
                  </button>
                ))}
              </div>
            )}
            {locationAddress && (
              <div className="mt-1 text-xs text-gray-500">{locationAddress}</div>
            )}
          </div>

          {/* Date & time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[30, 45, 60, 90, 120, 150, 180, 240].map((m) => (
                <option key={m} value={m}>{m >= 60 ? `${m / 60}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error instanceof Error ? error.message : 'Something went wrong'}</p>
          )}

          <div className="flex gap-2 pt-2">
            {isEditing && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !title}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
